export {
  GeolocationDetectionError,
  GeolocationDetectionErrorCode,
} from './lib/detectSupportedLocale';

export enum MicrophoneAccessType {
  UNKNOWN = 'UNKNOWN',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  ERROR = 'ERROR',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
}

/**
 * The locale region in which the current user is operating, as determined by
 * browser environment signals or GPS coordinates.
 *
 * Used throughout the app to gate features that are only available in the
 * United States and Canada (e.g. submitting bug reports).
 */
export enum SupportedLocale {
  /** The user's environment resolves to the United States. */
  UnitedStates = 'UnitedStates',
  /** The user's environment resolves to Canada. */
  Canada = 'Canada',
  /**
   * The user's environment could not be resolved to a supported region.
   * This may be a true negative (user is outside US/CA) or a false negative
   * caused by VPN usage, non-region browser locale settings, or GPS denial.
   * The `LocaleSource` field indicates how confident the determination is.
   */
  Unsupported = 'Unsupported',
}

/**
 * Describes how the current {@link LocaleState.locale} value was determined.
 *
 * The source drives UI behaviour: the "I'm in a supported region" escape-hatch
 * button is only shown when the source is `Inference`, because a geolocation
 * result — whether supported or not — is already the most authoritative signal
 * available and retrying would not produce new information.
 */
export enum LocaleSource {
  /**
   * Locale was inferred from browser heuristics at startup
   * (`navigator.language` + `Intl.DateTimeFormat` timezone).
   *
   * This is the initial default.  It can produce false negatives for users
   * behind VPNs or whose browser language does not match their physical region.
   * When the source is `Inference` and locale is `Unsupported`, the UI offers a
   * GPS-based escape hatch via {@link BrowserModule.attemptGeolocationDetection}.
   */
  Inference = 'Inference',
  /**
   * Locale was determined (or denied) by the browser Geolocation API.
   *
   * This value is set either by the silent auto-watch that starts on load when
   * permission is already granted, or by an explicit call to
   * {@link BrowserModule.attemptGeolocationDetection}.  GPS is treated as
   * authoritative: even a `Unsupported` result hides the escape hatch, since
   * the hardware confirmed the user's location.
   */
  Geolocation = 'Geolocation',
}

/**
 * All locale-detection state, grouped into a single sub-object on
 * {@link BrowserState} so that locale concerns are co-located and components
 * can subscribe to locale changes as a unit.
 *
 * Exposed reactively via `useBrowserState().localeState`.
 */
export type LocaleState = {
  /**
   * The currently detected locale region.
   *
   * Initialized at module load via timezone/language inference and may be
   * updated later by the geolocation watch or an explicit detection call.
   * When `source` is `Geolocation` this value is authoritative (GPS-confirmed).
   */
  locale: SupportedLocale;
  /**
   * How `locale` was last determined.
   *
   * Used by the BugReportModal to decide whether to render the "I'm in a
   * supported region" escape-hatch button: only shown when `source` is
   * `Inference`, i.e. the result has not yet been GPS-confirmed.
   */
  source: LocaleSource;
  /**
   * `true` while a geolocation position fix is in flight.
   *
   * Set to `true` by both {@link BrowserModule.attemptGeolocationDetection}
   * (user-triggered) and {@link BrowserModule.startGeolocationWatch} (silent
   * background watch, only when `source` is not already `Geolocation`).
   * Cleared to `false` by whichever path resolves or rejects first.
   *
   * The `localeLoading` derived value in `BugReportModal` intentionally guards
   * this with `locale === SupportedLocale.Unsupported` so that supported US/CA
   * users whose background watch hasn't yet fired are not affected.
   */
  isLoading: boolean;
};

export type BrowserState = {
  microphoneAccess: MicrophoneAccessType;
  tries: number;
  showMicrophoneAccessModal: boolean;
  isOnline: boolean;
  /**
   * All locale-detection state.
   *
   * Read reactively in components via `useBrowserState().localeState`.
   * Mutated by {@link BrowserModule} as geolocation signals arrive.
   * See {@link LocaleState} for field-level documentation.
   */
  localeState: LocaleState;
};
